/**
 * Theme — Variant Picker Web Component
 *
 * Handles option selection, variant resolution, and updates to:
 * - Hidden variant ID input
 * - Price display
 * - Add-to-cart button state (text + disabled)
 * - Option availability classes (strikethrough / greyed)
 * - URL parameters
 * - Gallery media (via variant:changed event)
 * - Option value display labels
 */

if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    class VariantPicker extends HTMLElement {
      constructor() {
        super();
        this.variants = [];
        this.currentVariant = null;
        this.sectionId = null;
        this.sectionRoot = null;
        this.unavailableStyle = 'strikethrough';
        this.shouldUpdateUrl = true;
      }

      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.unavailableStyle = this.dataset.unavailableStyle || 'strikethrough';
        this.shouldUpdateUrl = this.dataset.updateUrl !== 'false';
        this.sectionRoot =
          this.closest('.shopify-section') ||
          this.closest('.section') ||
          document;

        // Parse variant JSON embedded in the picker
        const variantJson = this.querySelector('[data-variant-json]');
        if (variantJson) {
          try {
            this.variants = JSON.parse(variantJson.textContent);
          } catch (e) {
            this.variants = [];
          }
        }

        // Sync initial UI state (JS overrides any stale server-rendered state)
        this.updateOptionAvailability();
        this.currentVariant = this.getSelectedVariant();
        this.updatePrice();
        this.updateBadges();
        this.updateBuyButton();

        this.addEventListener('change', this.onOptionChange.bind(this));
      }

      // ─── Option selection helpers ────────────────────────────────────────────

      /**
       * Returns { position: value } map from all checked radios + selects.
       */
      getSelectedOptions() {
        const options = {};

        this.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
          const pos = radio.dataset.optionPosition;
          if (pos) options[pos] = radio.value;
        });

        this.querySelectorAll('select[data-option-position]').forEach((select) => {
          const pos = select.dataset.optionPosition;
          if (pos) options[pos] = select.value;
        });

        return options;
      }

      /**
       * Find the variant that matches the current option selections.
       * Falls back to first variant if nothing is checked yet.
       */
      getSelectedVariant() {
        const options = this.getSelectedOptions();
        const positions = Object.keys(options);

        if (positions.length === 0) return this.variants[0] || null;

        return (
          this.variants.find((variant) =>
            positions.every((pos) => {
              const idx = parseInt(pos, 10) - 1;
              return variant.options[idx] === options[pos];
            })
          ) || null
        );
      }

      // ─── Change handler ──────────────────────────────────────────────────────

      onOptionChange(event) {
        const target = event.target;
        const isOptionControl =
          target.type === 'radio' ||
          (target.tagName === 'SELECT' && target.dataset.optionPosition);

        if (isOptionControl) {
          // Update the selected-value label display (e.g. "Color: Navy")
          this.updateSelectedValueDisplay(target);
          // Respect the shopper's exact selection. We intentionally do NOT
          // auto-change the other options: if the chosen combination is
          // sold out or doesn't exist, the Add-to-cart button reflects that
          // (disabled + "Sold out"/"Unavailable") instead of silently swapping
          // a value the shopper never touched. Matches Shopify's free themes.
        }

        const previousVariant = this.currentVariant;
        // Refresh availability styling after the (possibly adjusted) selection.
        this.updateOptionAvailability();
        this.currentVariant = this.getSelectedVariant();

        if (this.currentVariant) {
          if (this.currentVariant !== previousVariant) {
            this.updateVariantInput();
            this.updatePrice();
            this.updateBadges();
            if (this.shouldUpdateUrl) {
              this.updateUrl();
            }
            this.dispatchVariantChange();
          }
          this.updateBuyButton();
        } else {
          // No matching variant found
          this.updateBadges();
          this.updateBuyButton();
        }
      }

      // ─── Option availability ─────────────────────────────────────────────────

      /**
       * Style each option value according to availability. A value is
       * "available" when at least one purchasable variant matches it AND the
       * selected values of the *preceding* options only. Ignoring later options
       * keeps an earlier option (e.g. Color) from being visually locked by a
       * later selection (e.g. Size). Inputs are never `disabled` — every value
       * stays clickable so the shopper can freely select any combination; if
       * the chosen combination is sold out or missing, the Add-to-cart button
       * reflects that instead. Mirrors Dawn's option-availability behaviour.
       */
      updateOptionAvailability() {
        const currentOptions = this.getSelectedOptions();

        const isValueAvailable = (position, testValue) => {
          const idx = position - 1;
          return this.variants.some((variant) => {
            if (variant.options[idx] !== testValue) return false;
            if (!variant.available) return false;
            for (const pos in currentOptions) {
              const p = parseInt(pos, 10);
              if (p >= position) continue;
              if (variant.options[p - 1] !== currentOptions[pos]) return false;
            }
            return true;
          });
        };

        this.querySelectorAll(
          'input[type="radio"][data-option-position]'
        ).forEach((input) => {
          const position = parseInt(input.dataset.optionPosition, 10);
          const isAvailable = isValueAvailable(position, input.value);
          const isSwatch = !!input.closest('.variant-picker__swatches');

          input.classList.remove('is-unavailable', 'is-unavailable--strikethrough');

          if (!isAvailable) {
            if (isSwatch) {
              // Swatches use the greyed/slashed style regardless of setting,
              // matching the server-rendered markup.
              input.classList.add('is-unavailable');
            } else if (this.unavailableStyle === 'strikethrough') {
              input.classList.add('is-unavailable--strikethrough');
            } else if (this.unavailableStyle === 'greyed') {
              input.classList.add('is-unavailable');
            }
            // 'hide' is handled server-side in Liquid; we don't hide here
          }
        });

        // Keep dropdown options in sync — but do not hard-disable them, so a
        // reachable combination is never locked. Flag for styling only.
        this.querySelectorAll('select[data-option-position]').forEach((select) => {
          const position = parseInt(select.dataset.optionPosition, 10);
          Array.from(select.options).forEach((opt) => {
            const isAvailable = isValueAvailable(position, opt.value);
            opt.disabled = false;
            if (isAvailable) {
              opt.removeAttribute('data-unavailable');
            } else {
              opt.setAttribute('data-unavailable', 'true');
            }
          });
        });
      }

      // ─── UI update helpers ───────────────────────────────────────────────────

      /**
       * Update the "selected value" label text (e.g., "Color: Navy").
       */
      updateSelectedValueDisplay(target) {
        let optionName, selectedValue;

        if (target.type === 'radio') {
          // Name format: "option-{handle}-{position}"
          optionName = target.name.replace(/^option-/, '').replace(/-\d+$/, '');
          selectedValue = target.value;
        } else if (target.tagName === 'SELECT') {
          selectedValue = target.value;
          const label = target
            .closest('.variant-picker__option')
            ?.querySelector('.variant-picker__label span:first-child');
          optionName = label ? label.textContent.trim().toLowerCase() : '';
        }

        if (!optionName) return;

        this.querySelectorAll('[data-option-value-display]').forEach((el) => {
          const displayName = el.dataset.optionValueDisplay;
          if (
            displayName &&
            displayName.toLowerCase().replace(/-/g, '') ===
              optionName.replace(/-/g, '')
          ) {
            el.textContent = selectedValue;
          }
        });
      }

      /**
       * Update the hidden variant ID input in the product form.
       */
      updateVariantInput() {
        if (!this.currentVariant) return;

        const form = this.sectionRoot.querySelector(
          `[data-section-id="${this.sectionId}"] form, form[data-section-id="${this.sectionId}"]`
        );
        if (!form) return;

        const input =
          form.querySelector('input[data-variant-id]') ||
          form.querySelector('input[name="id"]');
        if (input) {
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      /**
       * Update all price display instances inside the section.
       */
      updatePrice() {
        if (!this.currentVariant) return;

        const priceGroups = this.sectionRoot.querySelectorAll('[data-product-price]');
        if (!priceGroups.length) return;

        const hasCompare =
          this.currentVariant.compare_at_price &&
          this.currentVariant.compare_at_price > this.currentVariant.price;

        priceGroups.forEach((group) => {
          const priceEl = group.querySelector('[data-price]');
          const comparePriceEl = group.querySelector('[data-compare-price]');

          if (priceEl) {
            priceEl.innerHTML = this.formatMoney(this.currentVariant.price);
          }

          if (!comparePriceEl) return;

          if (hasCompare) {
            comparePriceEl.innerHTML = this.formatMoney(
              this.currentVariant.compare_at_price
            );
            comparePriceEl.style.display = '';
          } else {
            comparePriceEl.innerHTML = '';
            comparePriceEl.style.display = 'none';
          }
        });
      }

      /**
       * Update PDP badge state (sold out / discount) on variant changes.
       * Badge markup is rendered by snippets/product-badges.liquid.
       */
      updateBadges() {
        const badgeGroup = this.sectionRoot.querySelector(
          '[data-product-badges][data-badge-context="product"]'
        );
        if (!badgeGroup) return;

        const soldOutEl = badgeGroup.querySelector('[data-badge-type="sold_out"]');
        const discountEl = badgeGroup.querySelector('[data-badge-type="discount"]');

        if (!this.currentVariant) {
          soldOutEl?.setAttribute('hidden', '');
          discountEl?.setAttribute('hidden', '');
          return;
        }

        const isSoldOut = !this.currentVariant.available;
        const compareAt = this.currentVariant.compare_at_price || 0;
        const price = this.currentVariant.price || 0;
        const isOnSale = compareAt > price;

        if (soldOutEl) {
          if (isSoldOut) {
            soldOutEl.removeAttribute('hidden');
          } else {
            soldOutEl.setAttribute('hidden', '');
          }
        }

        if (discountEl) {
          const showDiscount = !isSoldOut && isOnSale;

          if (!showDiscount) {
            discountEl.setAttribute('hidden', '');
            return;
          }

          const discountStyle = discountEl.dataset.discountStyle || 'percentage';
          const saleLabel = discountEl.dataset.labelSale || 'On sale';
          const percentTemplate =
            discountEl.dataset.templatePercent || '-[percent]%';
          let nextLabel = saleLabel;

          if (discountStyle === 'percentage') {
            const percent = compareAt > 0
              ? Math.round(((compareAt - price) * 100) / compareAt)
              : 0;
            nextLabel = percentTemplate.replace('[percent]', String(percent));
          }

          discountEl.textContent = nextLabel;
          discountEl.removeAttribute('hidden');
        }
      }

      /**
       * Update the add-to-cart button state and label.
       *
       * Reads translated strings from data attributes set in Liquid
       * (data-add-to-cart-text / data-sold-out-text / data-unavailable-text)
       * so we never hardcode untranslated keys in JS.
       */
      updateBuyButton() {
        const container = this.sectionRoot.querySelector('[data-buy-buttons]');
        if (!container) return;

        // Target the configured add-to-cart button first, then fall back to any submit button
        const btn =
          container.querySelector('[data-atc-btn]') ||
          container.querySelector('button[type="submit"]');
        if (!btn) return;

        // Three distinct states, matching Shopify's free themes:
        //   • available            → "Add to cart" (enabled)
        //   • exists but sold out  → "Sold out"    (disabled)
        //   • combination missing  → "Unavailable" (disabled)
        const variantExists = !!this.currentVariant;
        const isAvailable = !!(this.currentVariant && this.currentVariant.available);

        // Find label elements first so server-rendered text can be used as a safe source
        const mainLabelEl = btn.querySelector('.btn__label-text--main');
        const altLabelEl = btn.querySelector('.btn__label-text--alt');
        const plainLabelEl = btn.querySelector('.btn__label');
        const legacyLabelEl = btn.querySelector('.btn__text');

        const normalizeLabel = (value) => {
          let label = String(value || '')
            .replace(/&quot;/g, '"')
            .replace(/\s+/g, ' ')
            .trim();

          // Trim wrapping quotes (broken serialization edge case)
          label = label.replace(/^["']+/, '').replace(/["']+$/, '').trim();

          // Guard against truncated values like `"Add`
          if (label.toLowerCase() === 'add') return '';

          return label;
        };

        const serverRenderedLabel = normalizeLabel(
          mainLabelEl?.textContent ||
          plainLabelEl?.textContent ||
          legacyLabelEl?.textContent
        );

        // Retrieve translated strings stored as data attributes on the button
        const fallbackAddToCart =
          normalizeLabel(btn.dataset.addToCartText) ||
          normalizeLabel(window.theme?.strings?.addToCart) ||
          serverRenderedLabel ||
          'Add to cart';
        const fallbackSoldOut =
          normalizeLabel(btn.dataset.soldOutText) ||
          normalizeLabel(window.theme?.strings?.soldOut) ||
          'Sold out';
        const fallbackUnavailable =
          normalizeLabel(btn.dataset.unavailableText) ||
          normalizeLabel(window.theme?.strings?.unavailable) ||
          normalizeLabel(fallbackSoldOut);
        const addToCartText = normalizeLabel(fallbackAddToCart) || 'Add to cart';
        const soldOutText = normalizeLabel(fallbackSoldOut) || 'Sold out';
        const unavailableText = normalizeLabel(fallbackUnavailable) || 'Unavailable';

        // Toggle disabled state
        btn.disabled = !isAvailable;
        btn.setAttribute('aria-disabled', String(!isAvailable));

        // Update label text for global button markup and legacy fallbacks
        let nextLabel;
        if (isAvailable) {
          nextLabel = addToCartText;
        } else if (variantExists) {
          nextLabel = soldOutText; // real variant, out of stock
        } else {
          nextLabel = unavailableText; // combination doesn't exist
        }

        if (mainLabelEl) mainLabelEl.textContent = nextLabel;
        if (altLabelEl) altLabelEl.textContent = nextLabel;
        if (!mainLabelEl && !altLabelEl && plainLabelEl) {
          plainLabelEl.textContent = nextLabel;
        }
        if (legacyLabelEl) legacyLabelEl.textContent = nextLabel;
        if (!mainLabelEl && !altLabelEl && !plainLabelEl && !legacyLabelEl) {
          btn.textContent = nextLabel;
        }
      }

      /**
       * Format price in cents to a localized money string.
       * Uses Shopify's money_format from window globals.
       */
      formatMoney(cents) {
        if (typeof cents !== 'number') return '';

        const moneyFormat =
          window.theme?.moneyFormat ||
          window.Shopify?.money_format ||
          '${{amount}}';

        const amount = (cents / 100).toFixed(2);
        const amountNoDecimals = Math.floor(cents / 100).toString();
        const amountWithComma = amount.replace('.', ',');
        const amountNoDecimalsWithComma = amountNoDecimals.replace(
          /\B(?=(\d{3})+(?!\d))/g,
          ','
        );

        return moneyFormat
          .replace('{{amount_with_comma_separator}}', amountWithComma)
          .replace(
            '{{amount_no_decimals_with_comma_separator}}',
            amountNoDecimalsWithComma
          )
          .replace('{{amount_no_decimals}}', amountNoDecimals)
          .replace(/\{\{amount\}\}/g, amount); // use regex to replace all occurrences
      }

      // ─── URL & events ────────────────────────────────────────────────────────

      updateUrl() {
        if (!this.currentVariant) return;
        const url = new URL(window.location);
        url.searchParams.set('variant', this.currentVariant.id);
        window.history.replaceState({}, '', url.toString());
      }

      dispatchVariantChange() {
        if (!this.currentVariant) return;

        const event = new CustomEvent('variant:changed', {
          bubbles: true,
          detail: {
            variant: this.currentVariant,
            sectionId: this.sectionId,
          },
        });

        this.sectionRoot.dispatchEvent(event);
        document.dispatchEvent(event);
      }

    }
  );
}
